import { FilterOption, IrdEntitySchema, IrdFormat } from '@ird/shared-base';
import { GenericAdminOptions, SelectOptions } from '../../models';

/**
 * The format of a select or enum property: it reads as the value's label — the page's options for
 * `key` first, then the schema's allowed values. `(p) => p.format(selectFormat(schema, options, key))`.
 */
export function selectFormat<Dto>(
  schema: IrdEntitySchema<Dto>,
  options: GenericAdminOptions | undefined,
  key: string & keyof Dto,
): IrdFormat {
  const selectOptions = options?.[key] as SelectOptions | undefined;
  const allowedValues = schema.properties[key].allowedValues;
  return { kind: 'translation-key', render: (value) => selectLabel(value, selectOptions, allowedValues) };
}

/**
 * The label of a select value — the page's options first, then the declared allowed values, then the
 * value itself. A list reads as its items' labels. An empty label counts as none, at every step.
 */
export function selectLabel(value: unknown, selectOptions: SelectOptions | undefined, allowedValues: readonly FilterOption[]): string {
  if (Array.isArray(value)) {
    return value.map((item) => selectLabel(item, selectOptions, allowedValues)).join(', ');
  }

  // An object carries its own label under `labelKey`; without one it is looked up by its `id`.
  let key = value;
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const ownLabel = record[selectOptions?.labelKey ?? 'name'];
    if (typeof ownLabel === 'string' && ownLabel) {
      return ownLabel;
    }
    key = record['id'] ?? value;
  }

  const id = asText(key);
  return selectOptions?.labels?.[id] || allowedValues.find((option) => option.value === id)?.label || id;
}

/** An object has no text form of its own, so it reads as its JSON rather than `[object Object]`. */
function asText(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return value.toString();
  }
  return JSON.stringify(value) ?? '';
}
